import React, { useRef, useEffect } from "react";

// React wrapper for Om Design System TextField web component
const OmTextField = React.forwardRef(({ 
  label, 
  value, 
  onChange, 
  leadingIcon, 
  trailingIcon, 
  type = "text",
  placeholder,
  disabled = false,
  required = false,
  errorMessage,
  alwaysShowError = false,
  rows,
  ...props 
}, ref) => {
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handleInput = (event) => {
      if (onChange) {
        // Pass the original event object so parent components can access e.target.value
        onChange(event);
      }
    };

    const addEventListeners = () => {
      try {
        // Check if element is still connected before adding listeners
        if (!element || !element.isConnected) {
          return () => {};
        }
        
        // Listen for both input and change events from the web component
        element.addEventListener('input', handleInput);
        element.addEventListener('change', handleInput);
        
        return () => {
          if (element && element.isConnected) {
            try {
              element.removeEventListener('input', handleInput);
              element.removeEventListener('change', handleInput);
            } catch (error) {
              console.warn('Failed to remove event listeners from OmTextField:', error);
            }
          }
        };
      } catch (error) {
        console.warn('Failed to attach event listeners to OmTextField:', error);
        return () => {};
      }
    };

    // Check if component is ready or wait for it
    if (element.shadowRoot || element.isConnected) {
      return addEventListeners();
    } else {
      // Wait for component to be ready
      const timeout = setTimeout(addEventListeners, 100);
      return () => clearTimeout(timeout);
    }
  }, [onChange]);

  // Set value when it changes - but avoid infinite loops
  useEffect(() => {
    const element = elementRef.current;
    if (element && element.isConnected && value !== undefined && element.value !== value) {
      try {
        // Check if element is still valid before setting value
        if (element.setCustomValidity && typeof element.setCustomValidity === 'function') {
          element.value = value;
        }
      } catch (error) {
        console.warn('Failed to set value on OmTextField:', error);
      }
    }
  }, [value]);

  return (
    <omk-text-field
      ref={(el) => {
        elementRef.current = el;
        if (ref) {
          if (typeof ref === 'function') {
            ref(el);
          } else {
            ref.current = el;
          }
        }
      }}
      label={label}
      type={type}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      leading-icon={leadingIcon}
      trailing-icon={trailingIcon}
      custom-validation-message={errorMessage}
      always-show-validation-message={alwaysShowError}
      rows={rows}
      {...props}
    />
  );
});

OmTextField.displayName = 'OmTextField';

export default OmTextField;
